import {
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  isWithinInterval,
  format,
  parseISO,
} from "date-fns";
import { BOOKING_STATUS, PAYMENT_STATUS } from "../types/booking";

/**
 * Aggregate revenue by month for a date range
 * @param {Array} bookings - Array of booking objects
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} Array of { month, year, revenue }
 */
export const aggregateRevenueByMonth = (bookings, startDate, endDate) => {
  const monthlyRevenue = {};

  bookings.forEach((booking) => {
    if (!booking.eventDate) return;

    const eventDate =
      booking.eventDate instanceof Date
        ? booking.eventDate
        : booking.eventDate.toDate
        ? booking.eventDate.toDate()
        : parseISO(booking.eventDate);

    if (!isWithinInterval(eventDate, { start: startDate, end: endDate })) {
      return;
    }

    const monthKey = format(eventDate, "yyyy-MM");
    if (!monthlyRevenue[monthKey]) {
      monthlyRevenue[monthKey] = {
        month: format(eventDate, "MMM"),
        year: format(eventDate, "yyyy"),
        revenue: 0,
        count: 0,
      };
    }

    monthlyRevenue[monthKey].revenue += booking.totalAmount || 0;
    monthlyRevenue[monthKey].count += 1;
  });

  return Object.values(monthlyRevenue).sort((a, b) => {
    const dateA = new Date(`${a.year}-${a.month}-01`);
    const dateB = new Date(`${b.year}-${b.month}-01`);
    return dateA - dateB;
  });
};

/**
 * Aggregate bookings by month for a date range
 * @param {Array} bookings - Array of booking objects
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} Array of { month, year, count }
 */
export const aggregateBookingsByMonth = (bookings, startDate, endDate) => {
  const monthlyBookings = {};

  bookings.forEach((booking) => {
    if (!booking.eventDate) return;

    const eventDate =
      booking.eventDate instanceof Date
        ? booking.eventDate
        : booking.eventDate.toDate
        ? booking.eventDate.toDate()
        : parseISO(booking.eventDate);

    if (!isWithinInterval(eventDate, { start: startDate, end: endDate })) {
      return;
    }

    const monthKey = format(eventDate, "yyyy-MM");
    if (!monthlyBookings[monthKey]) {
      monthlyBookings[monthKey] = {
        month: format(eventDate, "MMM"),
        year: format(eventDate, "yyyy"),
        total: 0,
        byType: {},
        byStatus: {},
      };
    }

    monthlyBookings[monthKey].total += 1;

    // Count by event type
    const eventType = booking.eventType || "Other";
    monthlyBookings[monthKey].byType[eventType] =
      (monthlyBookings[monthKey].byType[eventType] || 0) + 1;

    // Count by status
    const status = booking.status;
    monthlyBookings[monthKey].byStatus[status] =
      (monthlyBookings[monthKey].byStatus[status] || 0) + 1;
  });

  return Object.values(monthlyBookings).sort((a, b) => {
    const dateA = new Date(`${a.year}-${a.month}-01`);
    const dateB = new Date(`${b.year}-${b.month}-01`);
    return dateA - dateB;
  });
};

/**
 * Get payment method distribution
 * @param {Array} bookings - Array of booking objects
 * @returns {Array} Array of { method, amount, percentage }
 */
export const getPaymentMethodDistribution = (bookings) => {
  const methodTotals = {};
  let totalAmount = 0;

  bookings.forEach((booking) => {
    if (booking.payments && booking.payments.length > 0) {
      booking.payments.forEach((payment) => {
        if (payment.status === PAYMENT_STATUS.VERIFIED) {
          const method = payment.paymentMethod || "Unknown";
          methodTotals[method] = (methodTotals[method] || 0) + payment.amount;
          totalAmount += payment.amount;
        }
      });
    }
  });

  return Object.entries(methodTotals).map(([method, amount]) => ({
    method,
    amount,
    percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
  }));
};

/**
 * Get event type distribution
 * @param {Array} bookings - Array of booking objects
 * @returns {Array} Array of { type, count, percentage }
 */
export const getEventTypeDistribution = (bookings) => {
  const typeCounts = {};
  let total = 0;

  bookings.forEach((booking) => {
    const eventType = booking.eventType || "Other";
    typeCounts[eventType] = (typeCounts[eventType] || 0) + 1;
    total += 1;
  });

  return Object.entries(typeCounts)
    .map(([type, count]) => ({
      type,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Get booking status distribution
 * @param {Array} bookings - Array of booking objects
 * @returns {Object} Status counts and percentages
 */
export const getStatusDistribution = (bookings) => {
  const statusCounts = {
    [BOOKING_STATUS.PENDING_DEPOSIT]: 0,
    [BOOKING_STATUS.CONFIRMED]: 0,
    [BOOKING_STATUS.COMPLETED]: 0,
    [BOOKING_STATUS.CANCELLED]: 0,
    [BOOKING_STATUS.NO_SHOW]: 0,
  };

  bookings.forEach((booking) => {
    if (booking.status && statusCounts.hasOwnProperty(booking.status)) {
      statusCounts[booking.status] += 1;
    }
  });

  const total = bookings.length;
  const statusWithLabels = Object.entries(statusCounts).map(([status, count]) => ({
    status,
    label: getStatusLabel(status),
    count,
    percentage: total > 0 ? (count / total) * 100 : 0,
  }));

  return {
    distribution: statusWithLabels,
    total,
    completedRate: total > 0 ? (statusCounts[BOOKING_STATUS.COMPLETED] / total) * 100 : 0,
    cancellationRate: total > 0 ? (statusCounts[BOOKING_STATUS.CANCELLED] / total) * 100 : 0,
  };
};

/**
 * Calculate financial summary
 * @param {Array} bookings - Array of booking objects
 * @returns {Object} Financial metrics
 */
export const calculateFinancialSummary = (bookings) => {
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);

  let totalRevenue = 0;
  let currentMonthRevenue = 0;
  let totalDepositCollected = 0;
  let totalPendingPayments = 0;
  let totalPaid = 0;
  let totalRemaining = 0;

  bookings.forEach((booking) => {
    const revenue = booking.totalAmount || 0;
    const paid = booking.paidAmount || 0;
    const remaining = booking.remainingAmount || 0;
    const deposit = booking.depositAmount || 0;

    totalRevenue += revenue;
    totalPaid += paid;
    totalRemaining += remaining;
    totalDepositCollected += deposit;

    // Check if booking is in current month
    const eventDate =
      booking.eventDate instanceof Date
        ? booking.eventDate
        : booking.eventDate?.toDate
        ? booking.eventDate.toDate()
        : null;

    if (eventDate && isWithinInterval(eventDate, { start: currentMonthStart, end: currentMonthEnd })) {
      currentMonthRevenue += revenue;
    }

    // Count pending payments
    if (booking.payments) {
      booking.payments.forEach((payment) => {
        if (payment.status === PAYMENT_STATUS.PENDING) {
          totalPendingPayments += payment.amount;
        }
      });
    }
  });

  return {
    totalRevenue,
    currentMonthRevenue,
    totalPaid,
    totalRemaining,
    totalDepositCollected,
    totalPendingPayments,
    averageRevenue: bookings.length > 0 ? totalRevenue / bookings.length : 0,
    depositCollectionRate: totalRevenue > 0 ? (totalPaid / totalRevenue) * 100 : 0,
  };
};

/**
 * Calculate booking summary
 * @param {Array} bookings - Array of booking objects
 * @returns {Object} Booking metrics
 */
export const calculateBookingSummary = (bookings) => {
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);

  let totalBookings = bookings.length;
  let currentMonthBookings = 0;

  const statusCounts = {
    [BOOKING_STATUS.PENDING_DEPOSIT]: 0,
    [BOOKING_STATUS.CONFIRMED]: 0,
    [BOOKING_STATUS.COMPLETED]: 0,
    [BOOKING_STATUS.CANCELLED]: 0,
    [BOOKING_STATUS.NO_SHOW]: 0,
  };

  bookings.forEach((booking) => {
    // Count by status
    if (booking.status && statusCounts.hasOwnProperty(booking.status)) {
      statusCounts[booking.status] += 1;
    }

    // Check if booking is in current month
    const eventDate =
      booking.eventDate instanceof Date
        ? booking.eventDate
        : booking.eventDate?.toDate
        ? booking.eventDate.toDate()
        : null;

    if (eventDate && isWithinInterval(eventDate, { start: currentMonthStart, end: currentMonthEnd })) {
      currentMonthBookings += 1;
    }
  });

  return {
    totalBookings,
    currentMonthBookings,
    averagePerMonth: totalBookings > 0 ? totalBookings / 12 : 0, // Rough estimate
    pendingDeposits: statusCounts[BOOKING_STATUS.PENDING_DEPOSIT],
    confirmed: statusCounts[BOOKING_STATUS.CONFIRMED],
    completed: statusCounts[BOOKING_STATUS.COMPLETED],
    cancelled: statusCounts[BOOKING_STATUS.CANCELLED],
  };
};

/**
 * Calculate growth rate
 * @param {number} currentValue - Current period value
 * @param {number} previousValue - Previous period value
 * @returns {number} Growth percentage
 */
export const calculateGrowthRate = (currentValue, previousValue) => {
  if (previousValue === 0) return currentValue > 0 ? 100 : 0;
  return ((currentValue - previousValue) / previousValue) * 100;
};

/**
 * Filter bookings by date range
 * @param {Array} bookings - Array of booking objects
 * @param {string} range - '6months', '1year', 'all'
 * @returns {Array} Filtered bookings
 */
export const filterBookingsByDateRange = (bookings, range = "all") => {
  const now = new Date();
  let startDate;

  switch (range) {
    case "6months":
      startDate = subMonths(now, 6);
      break;
    case "1year":
      startDate = subYears(now, 1);
      break;
    case "all":
    default:
      return bookings;
  }

  return bookings.filter((booking) => {
    if (!booking.eventDate) return false;

    const eventDate =
      booking.eventDate instanceof Date
        ? booking.eventDate
        : booking.eventDate.toDate
        ? booking.eventDate.toDate()
        : parseISO(booking.eventDate);

    return eventDate >= startDate;
  });
};

/**
 * Get status label
 * @param {string} status - Booking status
 * @returns {string} Human-readable status label
 */
const getStatusLabel = (status) => {
  const labels = {
    [BOOKING_STATUS.PENDING_DEPOSIT]: "Menunggu Deposit",
    [BOOKING_STATUS.CONFIRMED]: "Dikonfirmasi",
    [BOOKING_STATUS.COMPLETED]: "Selesai",
    [BOOKING_STATUS.CANCELLED]: "Dibatalkan",
    [BOOKING_STATUS.NO_SHOW]: "Tidak Hadir",
  };
  return labels[status] || status;
};
